import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import { Plus, Edit2, Building2, X, ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import {
  institutionService,
  InstitutionItem,
  CreateUpdateInstitutionPayload,
  GetInstitutionsPayload
} from '../services/adminAuth';
import { lookupService, LookupDto } from '../../../services/lookupService';
import { Button } from '../components/adminModals';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';

// ─── Constants ────────────────────────────────────────────────────────────────

const INSTITUTION_CATEGORIES: LookupDto[] = [
  { id: 1, name: 'University', active: true },
  { id: 2, name: 'Polytechnic', active: true },
  { id: 3, name: 'College', active: true },
];

const CATEGORY_COLORS: Record<string, string> = {
  University: 'bg-blue-50 text-blue-700',
  Polytechnic: 'bg-amber-50 text-amber-700',
  College: 'bg-emerald-50 text-emerald-700',
};

const PAGE_SIZE = 50;

const categoryIdByName = (name: string): number =>
  INSTITUTION_CATEGORIES.find(c => c.name === name)?.id ?? 0;

// ─── Main Component ───────────────────────────────────────────────────────────

const ManageInstitutions: React.FC = () => {
  const [states, setStates] = useState<LookupDto[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Form State
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState('');
  const [formCatId, setFormCatId] = useState('');
  const [formStateId, setFormStateId] = useState('');
  const [formIsPublic, setFormIsPublic] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formLoading, setFormLoading] = useState(false);

  // List & Filter State
  const [filterCatId, setFilterCatId] = useState('');
  const [filterIsPublic, setFilterIsPublic] = useState('');
  const [filterStateId, setFilterStateId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [institutions, setInstitutions] = useState<InstitutionItem[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  // Pagination (1-indexed)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [isFetchingList, setIsFetchingList] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const filtersReady = filterCatId !== '' && filterIsPublic !== '';

  const inputClass =
    'w-full bg-[#F3F4F6] rounded-lg py-3 px-4 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#00A63E] transition-all placeholder:text-gray-400';
  const labelClass = 'block text-sm font-medium text-gray-600 mb-1.5';

  useEffect(() => {
    const load = async () => {
      try {
        const data = await lookupService.getStates();
        setStates(data || []);
      } catch (error) {
        console.error("Failed to load states", error);
      }
    };
    load();
  }, []);

  const isFormValid =
    formName.trim().length > 0 &&
    formCatId !== '' &&
    formStateId !== '' &&
    formIsPublic !== '';

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormCatId('');
    setFormStateId('');
    setFormIsPublic('');
    setFormActive(true);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (inst: InstitutionItem) => {
    setEditingId(inst.id);
    setFormName(inst.name);
    setFormCatId(categoryIdByName(inst.institutionCategory).toString());
    setFormStateId(inst.stateId.toString());
    setFormIsPublic(inst.isPublic ? 'true' : 'false');
    setFormActive(inst.active);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const fetchInstitutions = useCallback(async (
    catId: string,
    isPublic: string,
    stateId: string,
    page: number,
    search: string
  ) => {
    if (!catId || !isPublic) return;
    setIsFetchingList(true);

    try {
      const payload: GetInstitutionsPayload = {
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: "name",
        sortDescending: false,
        search: search,
        categoryId: parseInt(catId, 10),
        stateId: stateId ? parseInt(stateId, 10) : 0,
        isPublic: isPublic === 'true'
      };

      const response = await institutionService.getInstitutions(payload);

      if (response.isSuccess && response.data) {
        const extractedList = response.data.data || [];
        const count = response.data.totalRecords || 0;

        setInstitutions(Array.isArray(extractedList) ? extractedList : []);
        setTotalItems(count);
      } else {
        setInstitutions([]);
        setTotalItems(0);
      }
      setListLoaded(true);
    } catch (error) {
      console.error("Failed to parse paginated payload:", error);
      setInstitutions([]);
      setTotalItems(0);
    } finally {
      setIsFetchingList(false);
    }
  }, []);

  useEffect(() => {
    if (filterCatId && filterIsPublic) {
      fetchInstitutions(filterCatId, filterIsPublic, filterStateId, currentPage, searchQuery);
    }
  }, [currentPage, filterCatId, filterIsPublic, filterStateId, searchQuery, fetchInstitutions]);

  const handleFilterChange = (catId: string, isPublic: string, stateId: string) => {
    setFilterCatId(catId);
    setFilterIsPublic(isPublic);
    setFilterStateId(stateId);
    setSearchQuery('');
    setCurrentPage(1);
    setInstitutions([]);
    setListLoaded(false);
  };

  const handleTableSearch = (query: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      setSearchQuery(query);
    }, 450);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    const payload: CreateUpdateInstitutionPayload = {
      id: editingId ?? 0,
      name: formName.trim(),
      isPublic: formIsPublic === 'true',
      active: formActive,
      stateId: parseInt(formStateId, 10),
      institutionCategoryId: parseInt(formCatId, 10),
    };

    setFormLoading(true);

    try {
      const response = editingId
        ? await institutionService.updateInstitution(payload)
        : await institutionService.createUpdateInstitution(payload);

      if (response.isSuccess) {
        await Swal.fire({
          icon: 'success',
          title: editingId ? 'Institution Updated!' : 'Institution Added!',
          text: response.message || 'Success',
          confirmButtonColor: '#00A63E',
        });

        resetForm();
        closeModal();

        if (filterCatId && filterIsPublic) {
          fetchInstitutions(filterCatId, filterIsPublic, filterStateId, currentPage, searchQuery);
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: response.message || 'Could not save institution.',
          confirmButtonColor: '#00A63E',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'An unexpected error occurred.',
        confirmButtonColor: '#00A63E',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const resolveStateName = (stateId: number) =>
    states.find(s => s.id === stateId)?.name ?? `State ID: ${stateId}`;

  // ─── Table Columns ─────────────────────────────────────────────────────────
  const columns: TableColumn<InstitutionItem>[] = [
    {
      key: 'name',
      label: 'Institution',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Building2 size={16} className="text-gray-500" />
          </div>
          <span className="font-medium text-gray-800">{row.name}</span>
        </div>
      ),
    },
    {
      key: 'institutionCategory',
      label: 'Category',
      render: (_, row) => {
        const color = CATEGORY_COLORS[row.institutionCategory] ?? 'bg-gray-100 text-gray-600';
        return (
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${color}`}>
            {row.institutionCategory}
          </span>
        );
      },
    },
    {
      key: 'stateId',
      label: 'State',
      sortValue: (row) => resolveStateName(row.stateId),
      render: (_, row) => (
        <span className="text-sm text-gray-600">{resolveStateName(row.stateId)}</span>
      ),
    },
    {
      key: 'isPublic',
      label: 'Type',
      render: (_, row) => (
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium
          ${row.isPublic ? 'bg-teal-50 text-teal-700' : 'bg-purple-50 text-purple-700'}`}>
          {row.isPublic ? 'Public' : 'Private'}
        </span>
      ),
    },
    {
      key: 'active',
      label: 'Status',
      render: (_, row) => (
        <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium
          ${row.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {row.active
            ? <><CheckCircle size={11} /> Active</>
            : <><XCircle size={11} /> Inactive</>}
        </span>
      ),
    },
  ];

  const actions: TableAction<InstitutionItem>[] = [
    {
      icon: <Edit2 size={12} />,
      label: 'Edit',
      onClick: (row) => openEditModal(row),
      variant: 'button',
      className: 'flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-400 hover:bg-white transition-all',
    },
  ];

  // ─── Filter Panel ──────────────────────────────────────────────────────────
  const filterPanel = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
      <div>
        <label className={labelClass}>Category *</label>
        <select
          value={filterCatId}
          onChange={e => handleFilterChange(e.target.value, filterIsPublic, filterStateId)}
          className={inputClass}
        >
          <option value="">Select category</option>
          {INSTITUTION_CATEGORIES.map(c => (
            <option key={c.id} value={c.id.toString()}>{c.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass}>Type *</label>
        <select
          value={filterIsPublic}
          onChange={e => handleFilterChange(filterCatId, e.target.value, filterStateId)}
          className={inputClass}
        >
          <option value="">Select type</option>
          <option value="true">Public</option>
          <option value="false">Private</option>
        </select>
      </div>

      <div>
        <label className={labelClass}>State (Optional)</label>
        <select
          value={filterStateId}
          onChange={e => handleFilterChange(filterCatId, filterIsPublic, e.target.value)}
          className={inputClass}
        >
          <option value="">All States</option>
          {states.map(s => (
            <option key={s.id} value={s.id.toString()}>{s.name}</option>
          ))}
        </select>
      </div>
    </div>
  );

  // ─── Empty / Loaded State Messages ─────────────────────────────────────────
  const emptyMessage = !filtersReady
    ? 'Select a category and type to browse institutions'
    : 'No institutions found for these filters';

  return (
    <div className="space-y-8">
      <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Manage Institutions
          </h1>
          <p className="text-gray-500 text-sm sm:text-base">
            Add, update, and browse registered institutions with pagination
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-start">
          <Button
            variant="success"
            onClick={openAddModal}
            className="w-full sm:w-auto flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Add Institution
          </Button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={closeModal} />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl z-10 overflow-hidden mx-4">
            <div className={`px-6 py-4 border-b flex items-center justify-between ${editingId ? 'bg-amber-50' : 'bg-white'}`}>
              <div className="flex items-center gap-2">
                {editingId ? <Edit2 size={16} className="text-amber-600" /> : <Plus size={16} className="text-[#00A63E]" />}
                <h2 className="text-sm font-semibold text-gray-800">
                  {editingId ? 'Edit Institution' : 'Add Institution'}
                </h2>
              </div>
              <button type="button" onClick={closeModal} className="text-gray-500 hover:text-gray-800">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className={labelClass}>Institution Name *</label>
                <input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. University of Lagos"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Category *</label>
                  <select value={formCatId} onChange={(e) => setFormCatId(e.target.value)} className={inputClass}>
                    <option value="">Select category</option>
                    {INSTITUTION_CATEGORIES.map(c => <option key={c.id} value={c.id.toString()}>{c.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>State *</label>
                  <select value={formStateId} onChange={(e) => setFormStateId(e.target.value)} className={inputClass}>
                    <option value="">Select state</option>
                    {states.map(s => <option key={s.id} value={s.id.toString()}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Institution Type *</label>
                <select value={formIsPublic} onChange={(e) => setFormIsPublic(e.target.value)} className={inputClass}>
                  <option value="">Select type</option>
                  <option value="true">Public</option>
                  <option value="false">Private</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || formLoading}
                  className="px-5 py-2 text-sm bg-[#00A63E] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#008c34] transition-colors"
                >
                  {formLoading ? 'Saving...' : editingId ? 'Save Changes' : 'Add Institution'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ReusableTable<InstitutionItem>
        title="Browse Institutions"
        subTitle={
          listLoaded && filtersReady
            ? `Showing ${institutions.length} of ${totalItems} (Page ${currentPage} of ${totalPages})`
            : 'Select category and type in the filter panel to begin'
        }
        columns={columns}
        data={filtersReady ? institutions : []}
        actions={actions}
        loading={isFetchingList}
        searchPlaceholder="Search institutions..."
        onSearch={handleTableSearch}
        emptyMessage={emptyMessage}
        showSearch
        showFilter
        filterPanel={filterPanel}
        isFilterOpen={isFilterOpen}
        onFilterToggle={() => setIsFilterOpen(o => !o)}
      />

      {filtersReady && listLoaded && institutions.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <button
            type="button"
            disabled={currentPage <= 1 || isFetchingList}
            onClick={() => handlePageChange(currentPage - 1)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed border rounded-lg px-3 py-1.5 bg-white transition-colors"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="text-sm text-gray-600 font-medium">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            disabled={currentPage >= totalPages || isFetchingList}
            onClick={() => handlePageChange(currentPage + 1)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed border rounded-lg px-3 py-1.5 bg-white transition-colors"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageInstitutions;
